// auth.interceptor.ts
import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError, switchMap } from 'rxjs';
import { SessionService } from './session.service';
import { AuthService } from './auth.service';

import { authBackendEndPointToken, backendEndPointToken, patientBackendEndPointToken } from '../../app.config';

const backendRoleMap: Record<string, string> = {
  'admin': 'hospitaladmin',
  'nurse': 'nursemidwife',
  'consultant': 'alliedhealthprofessional',
  'hospital admin': 'hospitaladmin',
  'health care assistant': 'healthcareassistant',
  'superadmin': 'superadmin',
  'hospitaladmin': 'hospitaladmin',
  'healthcareadmin': 'healthcareadmin',
  'doctor': 'doctor',
  'remote doctor': 'remotedoctor',
  'remotedoctor': 'remotedoctor'
};

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const sessionService = inject(SessionService);
  const authService = inject(AuthService);
  const backendUrl = inject(backendEndPointToken);
  const patientBackendUrl = inject(patientBackendEndPointToken);
  const authPatientBackendUrl = inject(authBackendEndPointToken);

  const patientBackendBase = patientBackendUrl?.replace(/\/fhir\/?$/, '');
  const backendBase = backendUrl?.replace(/\/fhir\/?$/, '');

  const isBackendRequest = req.url.startsWith(backendUrl) ||
    (!!backendBase && req.url.startsWith(backendBase)) ||
    (!!patientBackendBase && req.url.startsWith(patientBackendBase)) ||
    req.url.startsWith(patientBackendUrl) ||
    req.url.startsWith(authPatientBackendUrl);

  const isAuthEndpoint = req.url.includes('/auth/login') ||
    req.url.includes('/auth/register') ||
    req.url.includes('/auth/refresh') ||
    req.url.includes('/auth/forgot-password') ||
    req.url.includes('/auth/reset-password');

  if (!isBackendRequest || isAuthEndpoint) return next(req);

  // Builds the request with the token AND the active-role header, used for first try and retry
  const withAuth = (token: string | null) => {
    if (!token) return req;
    const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
    const role = sessionService.getSession()?.role?.toLowerCase().trim();
    if (role) headers['X-Active-Role'] = backendRoleMap[role] || role;
    return req.clone({ setHeaders: headers });
  };

  // Log out only when the server rejected the refresh, not on a network failure
  const onRefreshFailure = (e: any) => {
    const rejected = e instanceof HttpErrorResponse && (e.status === 400 || e.status === 401);
    if (rejected || e?.message === 'No refresh token available') authService.logout();
    return throwError(() => e);
  };

  return authService.getValidAccessToken$().pipe(
    catchError(onRefreshFailure),
    switchMap(token =>
      next(withAuth(token)).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status !== 401) return throwError(() => error);
          // Token rejected anyway: refresh once, retry once
          return authService.refreshAccessToken$().pipe(
            catchError(onRefreshFailure),
            switchMap(newToken => next(withAuth(newToken)))
          );
        })
      )
    )
  );
};
